import React, { useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
  Upload, BookOpen, Languages, Sparkles,
  Image as ImageIcon, Sigma, Loader2, ArrowLeft,
  AlertCircle, Type, FileWarning, Zap
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/* ===================== AI 引擎状态徽章 ===================== */
function AiBadge({ provider }) {
  if (!provider) return null;
  const isFree = provider.includes('workers') || provider.includes('ollama');
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
      isFree
        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
        : 'bg-amber-100 text-amber-700 border border-amber-200'
    }`}>
      <Zap className="w-3 h-3" />
      {isFree ? '免费引擎' : '付费引擎'} ・ {provider.replace('@cf/', '').replace('meta/', '')}
    </span>
  );
}

/* ===================== 公式渲染 + 变量解释组件 ===================== */
function FormulaBlock({ section }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-5">
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-5 sm:px-6 py-4 border-b border-violet-100">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Sigma className="w-4 h-4 text-white" />
          </div>
          <span className="text-xs font-bold text-violet-700 uppercase tracking-wider">公式 Formula</span>
        </div>
        <div className="text-center py-4 overflow-x-auto">
          <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
            {`$$${section.latex}$$`}
          </ReactMarkdown>
        </div>
      </div>
      <div className="px-5 sm:px-6 py-5">
        <div className="mb-5">
          <h4 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">公式物理意义</h4>
          <div className="text-gray-700 text-sm sm:text-base leading-loose">
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
              {section.explanation}
            </ReactMarkdown>
          </div>
        </div>
        {section.variables && section.variables.length > 0 && (
          <div className="mt-4">
            <h4 className="font-bold text-gray-900 mb-3 text-sm sm:text-base border-l-4 border-violet-400 pl-3">
              物理量逐项释义
            </h4>
            <div className="space-y-3">
              {section.variables.map((v, i) => (
                <div key={i} className="bg-gradient-to-r from-gray-50 to-violet-50/30 rounded-xl p-4 border border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1.5 bg-violet-100 text-violet-800 rounded-lg font-mono text-base font-bold">
                        {v.symbol}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 text-sm sm:text-base font-semibold">{v.meaning}</p>
                      {v.unit && (
                        <p className="text-gray-500 text-xs sm:text-sm mt-1">量纲 / 单位：{v.unit}</p>
                      )}
                      {v.description && (
                        <p className="text-gray-600 text-xs sm:text-sm mt-2 leading-relaxed">{v.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
/* ===================== 文本段落组件（原文 + 译文 + 详解） ===================== */
function TextBlock({ section }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-5">
      <div className="bg-gray-50 px-5 sm:px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-2">
          <Languages className="w-4 h-4 text-gray-400" />
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">原文 Original Text</span>
        </div>
        <p className="text-gray-600 text-sm sm:text-base leading-relaxed font-serif">{section.original}</p>
      </div>
      <div className="px-5 sm:px-6 py-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <Type className="w-3 h-3 text-white" />
          </div>
          <span className="text-xs font-bold text-primary-600 uppercase tracking-wider">译文 Chinese Translation</span>
        </div>
        <div className="text-gray-900 text-sm sm:text-base leading-loose">
          <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
            {section.translated}
          </ReactMarkdown>
        </div>
        {section.explanation && (
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50/50 px-5 sm:px-6 py-5 border-t border-blue-100 mt-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">深度解析 Deep Analysis</span>
            </div>
            <div className="text-gray-800 text-sm sm:text-base leading-loose">
              <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                {section.explanation}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================== 图片组件 ===================== */
function ImageBlock({ section }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-5">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-5 sm:px-6 py-4 border-b border-emerald-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <ImageIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">插图 Figure</span>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        {section.src ? (
          <div className="rounded-xl overflow-hidden border border-gray-200 mb-5">
            <img
              src={section.src}
              alt={section.caption_translated || ''}
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="rounded-xl bg-gray-100 border border-dashed border-gray-300 p-8 text-center mb-5">
            <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">原文图片占位</p>
          </div>
        )}
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <span className="text-xs text-gray-400 font-medium block mb-1.5">Original Caption</span>
            <p className="text-gray-600 text-sm italic leading-relaxed">{section.caption_original}</p>
          </div>
          <div>
            <span className="text-xs text-emerald-600 font-bold block mb-1.5">中文图注</span>
            <p className="text-gray-900 text-sm sm:text-base leading-relaxed">{section.caption_translated}</p>
          </div>
          {section.explanation && (
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
              <span className="text-xs text-emerald-700 font-bold block mb-2">图片科学解析</span>
              <p className="text-gray-800 text-sm sm:text-base leading-relaxed">{section.explanation}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================== 文档结果总览 ===================== */
function DocumentResult({ data, onReset }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 pb-20">
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-sm">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-900 text-sm sm:text-base">Learn 工具</span>
                {data._meta?.provider && <AiBadge provider={data._meta.provider} />}
              </div>
              <p className="text-[10px] text-gray-400 hidden sm:block">AI 翻译 ・ 公式解析 ・ 深度讲解</p>
            </div>
          </div>
          <button
            onClick={onReset}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            分析新文件
          </button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight">{data.title}</h1>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-xs sm:text-sm text-gray-500">AI 智能翻译与气象学科深度解析</span>
          </div>
        </div>
        {data.sections && data.sections.map((section, idx) => {
          if (section.type === 'formula') return <FormulaBlock key={idx} section={section} />;
          if (section.type === 'image') return <ImageBlock key={idx} section={section} />;
          return <TextBlock key={idx} section={section} />;
        })}
      </div>
    </div>
  );
}
/* ===================== 上传区域 ===================== */
function UploadZone({ onFileSelect, disabled }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) onFileSelect(f);
  }, [onFileSelect]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="text-center mb-10">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Learn 工具</h2>
        <p className="text-gray-500 text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
          上传 PDF 或 Word 文档，AI 将逐段翻译外文内容、深度解析公式物理意义，
          并提供专业的气象学科知识讲解。译文与原文对照呈现，公式逐项拆解，图片逐张提取。
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium border border-emerald-200">
          <Zap className="w-3.5 h-3.5" />
          默认使用 Cloudflare Workers AI 免费引擎
        </div>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-2xl p-10 sm:p-14 text-center cursor-pointer transition-all duration-200
          ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50 border-gray-200' :
            isDragOver
              ? 'border-primary-500 bg-primary-50 scale-[1.02] shadow-lg'
              : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 hover:shadow-md'
          }
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          className="hidden"
          disabled={disabled}
          onChange={(e) => e.target.files?.[0] && onFileSelect(e.target.files[0])}
        />
        <div className={`
          w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-colors
          ${isDragOver ? 'bg-primary-100' : 'bg-gray-100'}
        `}>
          <Upload className={`w-8 h-8 ${isDragOver ? 'text-primary-600' : 'text-gray-400'}`} />
        </div>
        <p className="text-gray-900 font-semibold text-base sm:text-lg mb-1">点击或拖拽上传文件</p>
        <p className="text-gray-400 text-sm">支持 PDF、Word（.doc / .docx）、TXT 格式</p>
        <p className="text-gray-400 text-xs mt-2">文件大小不得超过 {MAX_FILE_SIZE_MB} MB</p>
      </div>
    </div>
  );
}

/* ===================== 主页面 ===================== */
export default function LearnTool() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');

  const extractText = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'pdf') {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let text = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        setProgress(`正在提取 PDF 第 ${i} / ${pdf.numPages} 页文本...`);
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map(item => item.str).join(' ').replace(/\s+/g, ' ');
        text += pageText + '\n\n';
      }
      return text;
    }

    if (ext === 'docx') {
      setProgress('正在提取 Word 文档内容...');
      const mammoth = await import('mammoth');
      const arrayBuffer = await file.arrayBuffer();
      const res = await mammoth.default.extractRawText({ arrayBuffer });
      return res.value;
    }

    if (ext === 'txt') {
      setProgress('正在读取文本文件...');
      return await file.text();
    }

    throw new Error('不支持的文件格式，请上传 PDF、Word 或 TXT 文件');
  };

  const extractImages = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const images = [];

    if (ext === 'pdf') {
      setProgress('正在提取 PDF 中的图片...');
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const ops = await page.getOperatorList();

        for (let j = 0; j < ops.fnArray.length; j++) {
          if (ops.fnArray[j] === pdfjsLib.OPS.paintImageXObject) {
            const imgName = ops.argsArray[j][0];
            try {
              const img = await page.objs.get(imgName);
              if (img && img.data) {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                const imgData = ctx.createImageData(img.width, img.height);
                imgData.data.set(img.data);
                ctx.putImageData(imgData, 0, 0);
                const dataUrl = canvas.toDataURL('image/png');
                images.push({ src: dataUrl, page: i, name: imgName });
              }
            } catch (e) {
              // 某些图片格式可能不支持，跳过
            }
          }
        }
      }
    }

    if (ext === 'docx') {
      setProgress('正在提取 Word 文档中的图片...');
      const mammoth = await import('mammoth');
      const arrayBuffer = await file.arrayBuffer();
      const imageMap = {};

      const res = await mammoth.default.convertToHtml({
        arrayBuffer,
        convertImage: mammoth.default.images.imgElement((image) => {
          return image.read("base64").then((imageBuffer) => {
            const dataUrl = `data:${image.contentType};base64,${imageBuffer}`;
            imageMap[image.altText || `image_${Object.keys(imageMap).length}`] = dataUrl;
            return { src: dataUrl };
          });
        })
      });

      const parser = new DOMParser();
      const doc = parser.parseFromString(res.value, 'text/html');
      const imgEls = doc.querySelectorAll('img');
      imgEls.forEach((img, idx) => {
        if (img.src && img.src.startsWith('data:')) {
          images.push({
            src: img.src,
            alt: img.alt || `Figure ${idx + 1}`,
            page: 1
          });
        }
      });
    }

    return images;
  };

  const processFile = async (selectedFile) => {
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setError(`文件大小为 ${(selectedFile.size / 1024 / 1024).toFixed(1)} MB，超过了 ${MAX_FILE_SIZE_MB} MB 的限制。请压缩文件后重试。`);
      return;
    }

    setFile(selectedFile);
    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const [text, extractedImages] = await Promise.all([
        extractText(selectedFile),
        extractImages(selectedFile)
      ]);

      if (!text.trim()) throw new Error('未能从文件中提取到文本内容');

      setProgress('正在调用 AI 进行逐段翻译与深度分析，请耐心等待...');

      const response = await fetch('/api/learn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.slice(0, 35000),
          filename: selectedFile.name,
          images: extractedImages.slice(0, 20).map(img => ({
            src: img.src,
            alt: img.alt || '',
            page: img.page
          }))
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `请求失败 (${response.status})`);
      }

      const data = await response.json();

      if (extractedImages.length > 0 && data.sections) {
        let imgIdx = 0;
        data.sections = data.sections.map(section => {
          if (section.type === 'image' && imgIdx < extractedImages.length) {
            return { ...section, src: extractedImages[imgIdx++].src };
          }
          return section;
        });
      }

      setResult(data);
    } catch (err) {
      setError(err.message || '处理过程中发生未知错误');
    } finally {
      setIsProcessing(false);
      setProgress('');
    }
  };

  if (result) {
    return <DocumentResult data={result} onReset={() => { setResult(null); setFile(null); }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      <UploadZone onFileSelect={processFile} disabled={isProcessing} />

      {isProcessing && (
        <div className="max-w-md mx-auto px-4 pb-16">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-3" />
            <p className="text-gray-900 font-semibold mb-1">正在智能处理</p>
            <p className="text-gray-500 text-sm leading-relaxed">{progress}</p>
            <div className="mt-4 w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-full animate-pulse w-3/4" />
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-md mx-auto px-4 pb-16">
          <div className="bg-red-50 rounded-2xl border border-red-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <FileWarning className="w-5 h-5 text-red-500" />
              <p className="text-red-700 font-semibold text-sm">处理出错</p>
            </div>
            <p className="text-red-600 text-sm leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}